import React, { useState } from "react";
import { toast } from "sonner";
import { XCircle, Save, Truck } from "lucide-react";
import { api } from "../../lib/api";
import { useApiQuery, useQueryClient } from "../../lib/useApiQuery";
import { Loader, EmptyState } from "../../components/Loader";
import { PageHeader } from "../../components/PageHeader";

const STATUSES = ["pending", "confirmed", "shipped", "delivered", "completed"];

export default function AdminLiveOrders() {
  const qc = useQueryClient();
  const { data: orders = [], isLoading } = useApiQuery(["admin-live-orders"], "/admin/live-orders", { refetchInterval: 15000 });
  const [busy, setBusy] = useState(null);

  const reload = () => qc.invalidateQueries({ queryKey: ["admin-live-orders"] });

  const doCancel = async (o) => {
    if (!window.confirm("إلغاء هذا الطلب؟ سيتم إشعار البائع والمشتري.")) return;
    setBusy(o.order_id);
    try { await api(`/admin/orders/${o.order_id}/admin-cancel`, { method: "POST", body: "{}" }); toast.success("تم الإلغاء"); await reload(); }
    catch (e) { toast.error(e.message); } finally { setBusy(null); }
  };
  const changeStatus = async (o, status) => {
    setBusy(o.order_id);
    try { await api(`/admin/orders/${o.order_id}/admin-update`, { method: "PUT", body: JSON.stringify({ status }) }); toast.success("تم تحديث الحالة"); await reload(); }
    catch (e) { toast.error(e.message); } finally { setBusy(null); }
  };
  const changeFee = async (o) => {
    const v = window.prompt("رسوم التوصيل الجديدة (د.ل):", String(o.delivery_fee || 0));
    if (v === null) return;
    setBusy(o.order_id);
    try { await api(`/admin/orders/${o.order_id}/admin-update`, { method: "PUT", body: JSON.stringify({ delivery_fee: Number(v) }) }); toast.success("تم تحديث الرسوم"); await reload(); }
    catch (e) { toast.error(e.message); } finally { setBusy(null); }
  };
  const reassign = async (o) => {
    const v = window.prompt("معرّف السائق الجديد (user_id):", "");
    if (!v) return;
    setBusy(o.order_id);
    try { await api(`/admin/orders/${o.order_id}/admin-update`, { method: "PUT", body: JSON.stringify({ assigned_driver_id: v.trim() }) }); toast.success("تم إعادة الإسناد"); await reload(); }
    catch (e) { toast.error(e.message); } finally { setBusy(null); }
  };

  if (isLoading) return <Loader />;

  return (
    <div className="animate-fade-in-up max-w-3xl mx-auto">
      <PageHeader title="الطلبات الحالية" backTo="/admin" />
      <p className="text-xs text-[#6B7280] mb-3">تحديث لحظي — {orders.length} طلب نشط</p>
      {orders.length === 0 ? <EmptyState title="لا توجد طلبات نشطة" /> : (
        <div className="space-y-3">
          {orders.map((o) => (
            <div key={o.order_id} data-testid={`admin-order-${o.order_id}`} className="bg-white rounded-2xl border border-[#E8EAF2] p-4">
              <div className="flex items-center justify-between">
                <span className="font-black text-[#161629]">#{(o.order_id || "").slice(-6)}</span>
                <span className="text-xs px-2 py-1 rounded-full bg-[#F0F2FA] text-[#161629] font-bold">{o.status}</span>
              </div>
              <p className="text-sm text-[#6B7280] mt-1">المشتري: {o.buyer_name || "—"} • البائع: {o.seller_name || "—"}</p>
              <p className="text-sm text-[#161629] font-bold mt-1">الإجمالي: {Number(o.total || o.amount || 0).toFixed(2)} د.ل • توصيل: {Number(o.delivery_fee || 0).toFixed(2)}</p>
              <div className="flex flex-wrap gap-2 mt-3">
                <select data-testid={`admin-order-status-${o.order_id}`} defaultValue="" onChange={(e) => e.target.value && changeStatus(o, e.target.value)} className="text-xs border border-[#E8EAF2] rounded-lg px-2 py-1.5 bg-white">
                  <option value="">تغيير الحالة…</option>
                  {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
                <button data-testid={`admin-order-fee-${o.order_id}`} onClick={() => changeFee(o)} className="text-xs flex items-center gap-1 px-3 py-1.5 rounded-lg bg-[#F0F2FA] font-bold"><Save size={14} /> الرسوم</button>
                <button data-testid={`admin-order-reassign-${o.order_id}`} onClick={() => reassign(o)} className="text-xs flex items-center gap-1 px-3 py-1.5 rounded-lg bg-[#EAF1FF] text-[#3F8CFF] font-bold"><Truck size={14} /> إسناد سائق</button>
                <button data-testid={`admin-order-cancel-${o.order_id}`} disabled={busy === o.order_id} onClick={() => doCancel(o)} className="text-xs flex items-center gap-1 px-3 py-1.5 rounded-lg bg-[#FFE5EA] text-[#FF3D5A] font-bold disabled:opacity-50"><XCircle size={14} /> إلغاء</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
